import cx from "classnames";
import SectionCard, {
  SectionCardControls,
  SectionCardGroup
} from "../../../../Composites/SectionCard/SectionCard";
import { Table, Pagination, Button, Flex, Search, Text } from "@theorgchartdev/storybook";
import { DeleteUsers, GetUserList } from "@/Utils/Promises";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { EditUserModal } from "./EditUserModal";
import * as StateLib from "@/Utils/StateLib";
import { UserNameLabel } from "./UserNameLabel";
import { GetDisplayName, GetUsedSeats, userTypes } from "./Utils";
import { getLocalizedPaginationStrings, getLocalizedTableHeaderStrings } from "@/Utils/Utils";
import { appstr } from "@coreapp/utils/appstr";
import { QUserMgr } from "@coreapp/CoreApp/QUserMgr";
import { enumFeaturesList, kAdminUser } from "@coreapp/constants";
import { appstrp } from "@coreapp/utils/appstrp";
import { getFeatureAccess } from "@/Utils/Misc";

export const ManageUsers = ({ accountConfig }) => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [newUserMode, setNewUserMode] = useState(false);
  const [groups, setGroups] = useState(null);
  const [paginatedIndices, setPaginatedIndices] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  const wsMgr = StateLib.GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  const userInfo = wsMgr.GetUserInfo();
  const impersonateEnabled = accountConfig?.security?.adminimpersonation;
  const showImpersonate = userInfo?.GetUserRole() === kAdminUser && impersonateEnabled;

  const usedSeats = GetUsedSeats(users, groups);
  const seatCount = wsMgr.GetOrgInfo().GetSeatCount();
  const seatsString = usedSeats + " " + appstr("of") + " " + seatCount + " " + appstr("seatsused");

  const LoadUsers = async () => {
    const { userList, groupInfo } = await GetUserList();
    setGroups(groupInfo?.groups);
    setUsers(userList);
  };

  useEffect(() => {
    LoadUsers();
    eventTracker.ReportEvent("account_settings.manage_users.opened");
  }, []);

  const onTableDataChange = indices => {
    setPaginatedIndices(indices);
  };

  const openEditUserModal = user => {
    setEditUser(user);
    setShowModal(true);
  };

  const closeEditUserModal = reloadUsers => {
    setShowModal(false);
    setEditUser(null);
    setNewUserMode(false);
    if (reloadUsers) LoadUsers();
  };

  const createNewUser = () => {
    setNewUserMode(true);
    setShowModal(true);
  };

  const doDeleteUser = async username => {
    const delList = [username];
    const { success } = await DeleteUsers(delList);
    if (success) LoadUsers();
  };

  const handleDeleteUser = async user => {
    const username = user.username;
    const displayName = GetDisplayName(user);
    const hasDelegates = user.delegatedUsers && user.delegatedUsers.length > 0;

    const headerText = appstrp("Confirm delete", ` '${displayName}'`);
    const yesStr = hasDelegates ? appstr("confirmdeletedelegate") : appstr("Confirm delete");
    const noStr = hasDelegates ? appstr("denydeletedelegate") : appstr("Cancel");
    const infoText = hasDelegates ? (
      <>
        {appstr("deleteuserdelegate1")} <em>{appstr("accounts")}</em>
        {". "}
        {appstr("deleteuserdelegate2")}
      </>
    ) : (
      `${appstrp("deleteuser", ` '${displayName}' ${appstr("deleteuser2")}?`)}`
    );

    const otherProps = {
      alertProps: {
        severity: "warning",
        message: appstrp("permanentaction", ".")
      },
      minWidth: "medium"
    };

    wsMgr.CreateReactAlert(
      headerText,
      infoText,
      () => doDeleteUser(username),
      () => {},
      yesStr,
      noStr,
      null,
      otherProps
    );
  };

  const handleDownloadUsers = () => {
    const userMgr = new QUserMgr();
    const userObjList = userMgr.SetUserListFromJSON(filteredUsers, false);
    userMgr.ExportExcel(wsMgr, wsMgr.GetAccessGroupMgr(), userObjList);
  };

  const handleUserClick = (event, user) => {
    switch (event.detail) {
      case 1:
        break;
      case 2:
        openEditUserModal(user);
        break;
      default:
    }
  };

  const handlerManager = {
    editUser: openEditUserModal,
    deleteUser: handleDeleteUser,
    clickUser: handleUserClick
  };

  const gridOptions = {
    getRowStyle: (data, rowIndex, isHovered) => {
      if (isHovered) return { background: "#ececed", cursor: "pointer" };
    },
    onRowClick: handleUserClick
  };

  const UserLabel = ({ rowState }) => {
    return <UserNameLabel rowState={rowState} manager={handlerManager} />;
  };

  const BuildTableColumns = () => {
    return [
      {
        field: "username",
        headerName: appstr("userid"),
        type: "cellRenderer",
        columnWidth: "fifty",
        CellRenderer: UserLabel,
        filterDisabled: true
      },
      {
        field: "email",
        headerName: appstr("Email"),
        type: "overflowText",
        columnWidth: "twenty-five",
        filterDisabled: true,
        overflowTextProps: {
          clamp: 1
        }
      },
      {
        field: "name",
        headerName: appstr("Name"),
        columnWidth: "twenty-five",
        type: "overflowText",
        filterDisabled: true,
        overflowTextProps: {
          clamp: 1
        }
      },
      {
        field: "group",
        headerName: appstr("group"),
        columnWidth: "twenty-five",
        type: "overflowText",
        overflowTextProps: {
          clamp: 1
        },
        columnOptions: {
          getValue: data => {
            const groupId = data?.group;
            if (groupId === "0") return appstr("fullaccess");
            else return groups[groupId]?.name;
          },
          getFilterValue: data => {
            const groupId = data?.group;
            if (groupId === "0") return appstr("fullaccess");
            else return groups[groupId]?.name;
          }
        }
      },
      {
        field: "role",
        headerName: appstr("Role"),
        columnWidth: "twenty-five",
        type: "overflowText",
        overflowTextProps: {
          clamp: 1
        },
        columnOptions: {
          getValue: data => {
            const roleAssigned = data?.group && groups[data.group]?.roleassignment;
            const role = roleAssigned ?? data?.role;
            if (!role) return "";
            return roleAssigned
              ? `${appstr(userTypes[role])} (${appstr("Group Assigned")})`
              : appstr(userTypes[role]);
          },
          getFilterValue: data => {
            const roleAssigned = data?.group && groups[data.group]?.roleassignment;
            const role = roleAssigned ?? data?.role;
            if (!role) return "";
            return appstr(userTypes[role]);
          }
        }
      }
    ];
  };

  const displayUsers = useMemo(() => {
    return searchInput.length > 0
      ? users.filter(user => {
          const searchTerm = searchInput.toLowerCase();
          return (
            user?.name?.toLowerCase().indexOf(searchTerm) >= 0 ||
            user?.username?.toLowerCase().indexOf(searchTerm) >= 0 ||
            user?.email?.toLowerCase().indexOf(searchTerm) >= 0
          );
        })
      : users;
  }, [users, searchInput]);

  const sectionSearchBar = (
    <Flex direction={"row"} gap={"8px"}>
      <Text size={"medium"} colorModifier={"grey"} style={{ padding: "5px" }}>
        {seatsString}
      </Text>
      <Flex width={"300px"}>
        <Search
          clearable
          inputProps={{
            style: {
              fontSize: "14px",
              marginRight: "0",
              marginLeft: "auto"
            },
            spellCheck: false
          }}
          size={"medium"}
          onInput={value => setSearchInput(value)}
          placeholder={appstr("Find users")}
          timeout={200}
          onResetInput={() => setSearchInput("")}
          dataTestId={"user-table-search-input"}
        />
      </Flex>
    </Flex>
  );

  return (
    <SectionCardGroup>
      <SectionCardControls>
        <Flex width={"100%"} justifyContent={"space-between"}>
          <Button
            modifier={"secondary"}
            className={"oc-special-secondary"}
            iconProps={{ name: "user-plus" }}
            onClick={createNewUser}
            dataTestId={"create-user-button"}
            disabled={!getFeatureAccess(enumFeaturesList.addUsers)}
          >
            {appstr("Create new user")}
          </Button>
          <Button
            modifier={"secondary"}
            iconProps={{ name: "arrow-down-to-line" }}
            onClick={() => handleDownloadUsers()}
            tooltipProps={{
              message: appstr("exportuserstip")
            }}
            dataTestId={"export-users-button"}
          >
            {appstr("exportusers")}
          </Button>
        </Flex>
      </SectionCardControls>
      {(editUser || newUserMode) && (
        <EditUserModal
          accountConfig={accountConfig}
          groups={groups}
          show={showModal}
          existingUser={editUser}
          isCreateNewUser={newUserMode}
          onClose={closeEditUserModal}
          showImpersonate={showImpersonate}
          usersList={users}
        />
      )}
      <SectionCard
        title={appstr("users")}
        titleContent={sectionSearchBar}
        flexProps={{ gap: "0" }}
        customStyle={{ height: "100%", overflow: "hidden" }}
        maxHeight={"initial"}
      >
        <Flex direction={"column"} height={"100%"}>
          <div className={"ocn-file-manager-file-container"}>
            <div
              className={cx("ocn-file-manager-file-table", "ocn-list-scrollbar")}
              style={{ borderTop: "none" }}
            >
              <Table
                tableLayout={"fixed"}
                gridOptions={gridOptions}
                columns={BuildTableColumns()}
                data={displayUsers}
                showHeader={true}
                containerPadding={true}
                sortEnabled={true}
                rowSize={"largeR"}
                textSize={"mediumT"}
                filterEnabled={true}
                onFilterChange={setFilteredUsers}
                paginatedIndices={paginatedIndices}
                headerControlProps={{
                  textTags: getLocalizedTableHeaderStrings()
                }}
              />
              <Pagination
                modifier="full"
                data={filteredUsers}
                onDataChange={onTableDataChange}
                customStyle={{ padding: "12px 24px" }}
                startPage={1}
                textTags={getLocalizedPaginationStrings()}
                preserveSelectedPage
              />
            </div>
          </div>
        </Flex>
      </SectionCard>
    </SectionCardGroup>
  );
};
